import ReactDOMServer from 'react-dom/server';
import { Tooltip } from 'react-tooltip';
import { Common } from '@thecvlb/design-system/lib/src';
import classNames from 'classnames';

import { Medication } from 'models/medication.types';

type InsuranceMedicationCardProps = Medication & {
  dataTestId?: string;
  isActive?: boolean;
  loading?: boolean;
  onSelect: (id: string) => void;
};

const InsuranceMedicationCard: React.FC<InsuranceMedicationCardProps> = ({
  dataTestId,
  drug_name,
  onSelect,
  copay,
  isActive,
  loading,
  ...rest
}) => {
  const getColor = () => {
    if (!('status' in rest)) {
      return '';
    }
    switch (rest.status) {
      case 'PA Required':
        return 'text-yellow';
      case 'Not Covered':
        return 'text-red';
      case 'Covered':
        return 'text-green';
      default:
        return '';
    }
  };
  const getPrice = () => {
    if (!('status' in rest)) {
      return `$${copay}`;
    }
    switch (rest.status) {
      case 'PA Required':
        return 'Price TBD';
      case 'Not Covered':
        return `Cash pay: $${copay}`;
      case 'Covered':
        return `$${copay}`;
      default:
        return `$${copay}`;
    }
  };

  const getStatusText = () => {
    if (!('status' in rest)) {
      return '';
    }
    switch (rest.status) {
      case 'PA Required':
        return 'PA required';
      case 'Not Covered':
        return 'Not covered by your insurance';
      case 'Covered':
        return 'Covered';
      default:
        return '';
    }
  };

  return (
    <button
      className={classNames(
        'w-full rounded-2xl border px-6 py-4',
        isActive ? 'bg-primary-100' : 'bg-white'
      )}
      data-testid="medication"
      onClick={() => onSelect(drug_name)}
    >
      <Common.RadioButton
        checked={isActive}
        color="blue"
        dataTestId="checkbox"
        disabled={loading}
        onChange={() => {}}
      >
        <div className="ml-1 flex flex-col items-start gap-0.5">
          {'oldPrice' in rest ? (
            <span className="text-mSm font-bold text-green md:text-sm">
              <span className="mr-1 font-medium text-gray line-through">{rest.oldPrice}</span>
              {getPrice()}
            </span>
          ) : (
            <span className="text-left text-mSm md:text-sm" data-testid="price">
              {getPrice()}
            </span>
          )}
          <b className="text-left" data-testid="drug_name">
            {drug_name}
          </b>
          {'status' in rest && (
            <div className="flex items-center gap-0.5 text-mSm md:text-sm">
              <span className={getColor()} data-testid="status">
                {getStatusText()}
              </span>
              {rest.status === 'PA Required' && (
                <button
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                  }}
                >
                  <Tooltip
                    className="max-w-[200px] !rounded-xl !bg-black !p-3 !text-white !shadow-sm"
                    classNameArrow="w-4 h-4"
                    id="name"
                  />
                  <div
                    data-tooltip-html={ReactDOMServer.renderToStaticMarkup(
                      <p>
                        <b>Prior Authorization (PA)</b> is required from your insurance company in
                        order to cover this medication. We can work with you to request this
                        authorization.
                      </p>
                    )}
                    data-tooltip-id={'name'}
                  >
                    <span className="block pl-0.5 pt-0.5">
                      <Common.Icon className="text-yellow-400" name="info" />
                    </span>
                  </div>
                </button>
              )}
            </div>
          )}
        </div>
      </Common.RadioButton>
    </button>
  );
};

export default InsuranceMedicationCard;
